import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Truck, Upload } from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common/PageHeader";
import { EmptyState } from "@/components/common/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

export default function Suppliers() {
  const { t } = useI18n();
  const { can } = useAuth();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", datev_account: "", currency: "EUR", payment_terms_days: 30 });
  const { data, isLoading } = useQuery({ queryKey: ["suppliers"], queryFn: async () => (await api.get("/suppliers")).data });

  const create = async () => {
    try {
      await api.post("/suppliers", form);
      toast.success(t("g2.created"));
      setOpen(false); setForm({ name: "", email: "", datev_account: "", currency: "EUR", payment_terms_days: 30 });
      qc.invalidateQueries({ queryKey: ["suppliers"] });
    } catch (e) { toast.error(apiErrorMessage(e, t)); }
  };

  return (
    <div>
      <PageHeader title={t("nav.suppliers")} subtitle={t("g2.suppliers_sub")}
        actions={can("suppliers:write") && (<>
          <Button variant="outline" onClick={() => navigate("/data-import/suppliers")} data-testid="import-suppliers-button">
            <Upload className="h-4 w-4" />{t("data_import.import")}
          </Button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="new-supplier-button"><Plus className="h-4 w-4" />{t("g2.new_supplier")}</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>{t("g2.new_supplier")}</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div className="space-y-1.5"><Label>{t("common.name")} *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="supplier-name-input" /></div>
                <div className="space-y-1.5"><Label>{t("common.email")}</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
                <div className="space-y-1.5"><Label>{t("g2.datev_creditor")}</Label><Input inputMode="numeric" value={form.datev_account} onChange={(e) => setForm({ ...form, datev_account: e.target.value.replace(/\D/g, "").slice(0, 9) })} placeholder={t("g2.datev_creditor_hint")} /></div>
              </div>
              <DialogFooter><Button onClick={create} disabled={!form.name} data-testid="supplier-save-button">{t("common.save")}</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        </>)} />
      <Card>
        {isLoading ? <div className="p-6 text-sm text-slate-400">{t("common.loading")}</div>
          : data.items.length === 0 ? <EmptyState icon={Truck} title={t("g2.no_suppliers")} testId="suppliers-empty" />
          : (
          <Table data-testid="suppliers-table">
            <TableHeader><TableRow><TableHead>{t("g2.number")}</TableHead><TableHead>{t("common.name")}</TableHead><TableHead>{t("common.email")}</TableHead><TableHead>{t("common.currency")}</TableHead></TableRow></TableHeader>
            <TableBody>
              {data.items.map((s) => (
                <TableRow key={s.id} data-testid={`supplier-row-${s.id}`}>
                  <TableCell className="font-medium tabular-nums">{s.supplier_number}</TableCell>
                  <TableCell>{s.name}</TableCell><TableCell className="text-slate-500">{s.email || "—"}</TableCell><TableCell>{s.currency}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  );
}
